// frontend/src/components/Samples/SampleOrderCalendar.jsx
// Sample orders calendar plugged into the generic CalendarView.
// Route: /samples/calendar

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { sampleOrdersAPI } from '../../api/client';
import { useTranslation } from '../../context/TranslationContext';
import CalendarView from '../Common/CalendarView';

// ── Status colours matching sample order statuses ────────────────────────────
const STATUS_COLOR = {
  OPEN:      '#2563eb',
  RELEASED:  '#059669',
  CANCELLED: '#dc2626',
  CLOSED:    '#6b7280',
};

const STATUS_LABEL = {
  OPEN:      'Aberto',
  RELEASED:  'Lançado',
  CANCELLED: 'Cancelado',
  CLOSED:    'Fechado',
};

const toYYYYMM = (year, month) =>
  `${year}-${String(month + 1).padStart(2, '0')}`;

const SampleOrderCalendar = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Track the currently displayed month (JS month = 0-based)
  const today = new Date();
  const [viewYear,  setViewYear]  = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const [orders,  setOrders]  = useState([]);
  const [loading, setLoading] = useState(true);

  // Reload whenever the viewed month changes
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const deliveryMonth = toYYYYMM(viewYear, viewMonth);
        const res = await sampleOrdersAPI.getForCalendar(deliveryMonth);
        if (!cancelled && res.data.success) {
          setOrders(res.data.data || []);
        }
      } catch { /* silent — calendar shows empty */ }
      finally { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [viewYear, viewMonth]);

  // Called by CalendarView when user navigates to another month
  const handleMonthChange = useCallback((year, month) => {
    setViewYear(year);
    setViewMonth(month);
  }, []);

  // Map sample orders → CalendarView events
  const events = useMemo(() => orders
    .filter(o => o.requested_delivery_date)
    .map(o => ({
      id:      o.public_id || o.sample_order_id,
      title:   [o.sample_order_no, o.customer_name].filter(Boolean).join(' - '),
      date:    o.requested_delivery_date.slice(0, 10),
      color:   STATUS_COLOR[o.status] || '#6b7280',
      badge:   STATUS_LABEL[o.status] || o.status,
      tooltip: [
        o.sample_order_no,
        o.customer_name,
        o.sample_order_description,
        STATUS_LABEL[o.status],
      ].filter(Boolean).join(' · '),
      onClick: () => navigate(`/samples/orders/${o.public_id}`),
    })),
  [orders, navigate]);

  return (
    <div className="page-container">
      <CalendarView
        events={events}
        loading={loading}
        title={t('sample_orders.calendar_title', 'Calendário de Encomendas')}
        onMonthChange={handleMonthChange}
        initialYear={viewYear}
        initialMonth={viewMonth}
      />
    </div>
  );
};

export default SampleOrderCalendar;